import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/cn";
import { codigoDoPedido } from "@/lib/codigo-do-pedido";
import { STATUS_META } from "@/components/prosthesis/prosthesis-meta";
import type { IncomingOrderRow } from "./production-types";
import { rotuloDoMaterialDoServico } from "@/components/prosthesis/rotulo-do-material";

type Props = {
  orders: IncomingOrderRow[];
  onViewDetail: (order: IncomingOrderRow) => void;
};

const dataCurta = (v: Date | string | null | undefined) =>
  v ? new Date(v).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" }) : null;

/** Serviço · Material · Cor do pedido, numa linha só. */
function resumoDosServicos(order: IncomingOrderRow): string {
  const partes = (order.services ?? []).map((s: any) =>
    [s.serviceCategory?.label, rotuloDoMaterialDoServico(s), s.shadeOption?.code]
      .filter(Boolean)
      .join(" · "),
  );
  return partes.filter(Boolean).join(" • ");
}

/**
 * Modo Lista dos pedidos recebidos, no celular.
 *
 * A tabela tem oito colunas com larguras percentuais fixas (`table-fixed`, `w-[11%]`…). Em 360px
 * cada coluna fica com ~35px: o cabeçalho se sobrepõe, o nome do paciente quebra uma letra por
 * linha e a data quebra na vertical. Reduzir fonte não resolve — a coluna continua estreita.
 *
 * Aqui cada pedido é uma linha de quatro faixas: paciente, clínica, serviços e situação com data.
 * Mais compacta que o cartão e sem coluna nenhuma. A tabela segue intacta de `md` pra cima.
 */
export function IncomingOrdersMobileList({ orders, onViewDetail }: Props) {
  return (
    <ul className="divide-y divide-zinc-100 overflow-hidden rounded-lg border border-zinc-200 bg-white dark:divide-border/50 dark:border-border dark:bg-card">
      {orders.map((o) => {
        const meta = STATUS_META[o.status as keyof typeof STATUS_META];
        const servicos = resumoDosServicos(o);
        const quando = dataCurta(o.deadline) ?? dataCurta(o.createdAt);

        return (
          <li key={o.id}>
            <button
              type="button"
              onClick={() => onViewDetail(o)}
              aria-label={`Abrir pedido de ${o.patientName}`}
              className="flex min-h-11 w-full items-start gap-2 px-3 py-3 text-left transition-colors hover:bg-zinc-50 dark:hover:bg-muted/50"
            >
              <span className="min-w-0 flex-1">
                <span className="block break-words text-sm font-medium leading-snug text-zinc-900 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] overflow-hidden dark:text-foreground">
                  <span className="mr-1.5 font-mono text-[11px] font-semibold text-zinc-400 dark:text-muted-foreground">#{codigoDoPedido(o)}</span>
                  {o.patientName}
                </span>

                {/* Clínica e dentista na mesma linha de apoio — é como o pedido é identificado
                    de fora, e a tabela mostrava as duas em colunas separadas. */}
                <span className="mt-0.5 block truncate text-xs text-zinc-500 dark:text-muted-foreground">
                  {[o.organization.name, o.dentistName].filter(Boolean).join(" · ")}
                </span>

                {servicos && (
                  <span className="mt-0.5 block break-words text-xs leading-snug text-zinc-500 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] overflow-hidden dark:text-muted-foreground">
                    {servicos}
                  </span>
                )}

                <span className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                  {meta && (
                    <span
                      className={cn(
                        "inline-flex shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase",
                      )}
                      style={{ backgroundColor: `${meta.color}1a`, color: meta.color }}
                    >
                      {meta.label}
                    </span>
                  )}
                  {quando && (
                    <span className="text-[11px] tabular-nums text-zinc-500 dark:text-muted-foreground">
                      {o.deadline ? `Prazo ${quando}` : quando}
                    </span>
                  )}
                </span>
              </span>

              <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-zinc-300 dark:text-muted-foreground/60" />
            </button>
          </li>
        );
      })}
    </ul>
  );
}
